import Application from '../Application';
import BaseView, { DomainModel3D } from './BaseView';
import CountertopView from './CountertopView';
import WallView from './WallView';
import mfac from '../MaterialFactory';
import pc from '../ParametricCache';
import { Mesh } from 'three';
import { Room } from '../domain';
import { approx } from '../measurement';
import CrownMoldingView from './CrownMoldingView';

const rot90 = (x: number, y: number) => [-y, x];

// Adapted from https://en.wikipedia.org/wiki/Intersection_(geometry)#Two_lines
// xs, ys is the intersection of two lines a1*x + b1*y = c1, and
// a2*x + b2*y = c2. In our case, b1 and b2 are always one. The general formula
// are:
// xs = (c1*b2 - c2*b1) / (a1*b2 - a2*b1)
// ys = (a1*c2 - a2*c1) / (a1*b2 - a2*b1)
const intersect = (
    a1: number, c1: number, a2: number, c2: number): [number, number] =>
{
    return [
        (c1 - c2) / (a1 - a2),
        (a1*c2 - a2*c1) / (a1 - a2)
    ];
}

export default class RoomView extends BaseView {

    declare model: Room;

    walls: WallView[];
    fillers: Mesh[];

    constructor(model: DomainModel3D, app: Application) {
        super(model, app);
    
        this.walls = [];
        this.fillers = [];

        this.init();
    }

    make() {
        let last: number = Infinity;
        for(let wall of this.model.walls) {
            const hideStart = approx(Math.PI/2, wall.rotation.y - last);
            const view = new WallView(wall, this.app, hideStart);
            this.walls.push(view);
            this.add(view);

            last = wall.rotation.y;
        }

        if(this.model.walls.length > 0) {
            // Iterate through the walls in adjacent pairs
            for(let i=1; i < this.model.walls.length; i++) {
                const a = this.model.walls[i-1];
                const b = this.model.walls[i];
                // ignore corners adjacent to islands or invisible walls
                if(a.island || b.island) {
                    continue;
                }
                if(!(a.visible && b.visible)) {
                    continue;
                }
                // If b is rotated clockwise (negative) more than a, then the
                // bend in the wall is clockwise, and we need a filler.
                if(b.rotation.y - a.rotation.y < 0) {
                    const
                        height = b.heights[0].height,
                        [ax, ay] = rot90(
                            Math.cos(a.rotation.y)*4.5,
                            Math.sin(a.rotation.y)*4.5
                        ),
                        [bx, by] = rot90(
                            Math.cos(b.rotation.y)*4.5,
                            Math.sin(b.rotation.y)*4.5
                        ),
                    // the slopes of a and b
                    am = Math.tan(a.rotation.y),
                    bm = Math.tan(b.rotation.y),

                    // solve the familiar `y = mx + b` for b given known
                    // points. In terms of our variables, this means solving
                    // `ay = am*ax + ac` for ac. The ac variable becomes a
                    // parameter to the `intersect` function which uses the
                    // general form of a line `ay + bx + c = 0` to solve for
                    // a two line intersection.
                    ac = -am*ax + ay,
                    bc = -bm*bx + by,
                    // c for "corner"
                    [cx, cy] = intersect(-am, ac, -bm, bc);

                    pc.make('WallCornerFill', height, ax, ay, bx, by, cx, cy)
                        .then(geo => {
                            const mesh = new Mesh(geo, mfac.make('WallPaint'))
                            mesh.castShadow = mesh.receiveShadow = true;
                            mesh.position.set(
                                b.position.x, b.position.y, b.position.z);
                            this.fillers.push(mesh);
                            this.add(mesh);
                        });
                }
            }
        }

        this.add(new CountertopView(this.model, this.app));
        this.add(new CrownMoldingView(this.model, this.app));
    }

    bind() {
        // nothing to do here ... the fillers won't really work if the walls
        // move dynamically. For now we don't anticipate the floor plan
        // changing in a live 3D view.
    }

    /**
     * Searches the room's walls for a wall with the given `wallOrder` and sets
     * the view's visibility to `isVisible`. This will make the wall and all of
     * its children invisible. Note that the Wall.visible domain model property
     * is used for making only the wall mesh invisible, but it will still
     * display the wall's children. This supports islands and peninsulas, not
     * for interactively toggling whether the object is visible.
    */
    setWallViewVisible(wallOrder: number, isVisible: boolean) {
        for(let wall of this.walls) {
            if(wall.model.wallOrder == wallOrder) {
                wall.visible = isVisible;
            }
        }
        // Request that the render pipeline update the display because we just
        // made a change
        Application.do('pipeline-update');
    }
}
